import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import GeneratorForm from '@/components/GeneratorForm';
import ResultDisplay from '@/components/ResultDisplay';
import SavedDocuments from '@/components/SavedDocuments';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import Footer from '@/components/Footer';
import { Toaster } from '@/components/ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import CollaborativeDocuments from '@/components/collaboration/CollaborativeDocuments';
import LegalChatbot from '@/components/LegalChatbot';
import DocumentScanner from '@/components/DocumentScanner';
import SharedDocuments from '@/components/SharedDocuments';
import FeedbackDialog from '@/components/feedback/FeedbackDialog';
import FeedbackStats from '@/components/feedback/FeedbackStats';
import QRCodeShare from '@/components/QRCodeShare';
import { Bot, ScanText, Share2, FileEdit, Save } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileNavigation from '@/components/MobileNavigation';
import ConnectionStatusIndicator from '@/components/mobile/ConnectionStatusIndicator';

// Keep IndexContent the same except for removing ConnectionStatusIndicator
const IndexContent = () => {
  const [generatedResult, setGeneratedResult] = useState('');
  const [activeTab, setActiveTab] = useState('create');
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  
  // Animation classes for language transition
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    setFadeIn(true);
    const timer = setTimeout(() => setFadeIn(false), 500);
    return () => clearTimeout(timer);
  }, [language]);

  // Handle iOS viewport height issue
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  const handleResult = (result: string) => {
    setGeneratedResult(result);
    setActiveTab('create');
  };

  const handleSelectSavedDocument = (content: string) => {
    setGeneratedResult(content);
    setActiveTab('create');
  };

  return (
    <div 
      className={`min-h-screen bg-adalat-gradient ${language === 'ur' ? 'font-urdu' : 'font-english'}`}
      style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}
    >
      <Toaster position={isMobile ? "bottom-center" : "top-center"} />
      {!isMobile && <ConnectionStatusIndicator />}
      
      <div className={`container mx-auto ${isMobile ? 'py-4 px-4' : 'py-8'} ${fadeIn ? 'fade-in' : ''}`}>
        <div className={`flex ${isMobile ? 'flex-col gap-4' : 'justify-end items-center gap-2'} mb-4`}>
          {isMobile && <Header />}
          <div className={`flex ${isMobile ? 'w-full justify-between' : 'items-center gap-2'}`}>
            <FeedbackStats />
            <div className="flex gap-2">
              {!isMobile && <QRCodeShare />}
              <FeedbackDialog />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
        
        {!isMobile && <Header />}
        
        {/* Informative Content Section */}
        <div className={`${language === 'ur' ? 'rtl' : 'ltr'} mb-8`}>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-lg shadow-sm">
              <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-bold text-blue-700 mb-3`}>
                {language === 'ur' ? 'آسان اور تیز' : 'Easy & Fast'}
              </h3>
              <p className={`${language === 'ur' ? 'text-urdu-base text-right' : 'text-base'} text-gray-700`}>
                {language === 'ur' 
                  ? 'صرف چند منٹ میں اپنی قانونی دستاویز تیار کریں۔ کوئی پیچیدہ فارم، کوئی قانونی زبان کی رکاوٹ نہیں۔'
                  : 'Generate your legal document in just a few minutes. No complex forms, no legal jargon barriers.'}
              </p>
            </div>
            
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-lg shadow-sm">
              <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-bold text-blue-700 mb-3`}>
                {language === 'ur' ? 'پاکستانی قوانین' : 'Pakistani Laws'}
              </h3>
              <p className={`${language === 'ur' ? 'text-urdu-base text-right' : 'text-base'} text-gray-700`}>
                {language === 'ur' 
                  ? 'تمام دستاویزات پاکستان کے قوانین کے مطابق تیار کی جاتی ہیں۔ درست، قابل استعمال اور عدالت میں پیش کرنے کے قابل۔'
                  : 'All documents are prepared according to Pakistani law. Accurate, usable, and court-ready.'}
              </p>
            </div>
            
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-lg shadow-sm">
              <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-bold text-blue-700 mb-3`}>
                {language === 'ur' ? 'مفت خدمات' : 'Free Services'}
              </h3>
              <p className={`${language === 'ur' ? 'text-urdu-base text-right' : 'text-base'} text-gray-700`}>
                {language === 'ur' 
                  ? 'تمام بنیادی خدمات مکمل طور پر مفت ہیں۔ کوئی چھپی ہوئی فیس نہیں، کوئی سبسکرپشن نہیں۔'
                  : 'All basic services are completely free. No hidden fees, no subscriptions required.'}
              </p>
            </div>
          </div>
        </div>
        
        <main className={`mt-4 ${isMobile ? 'mb-24' : 'mb-16'} ${language === 'ur' ? 'rtl' : 'ltr'}`}>
          {!isMobile ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className={`flex justify-center mb-6 ${isMobile ? 'overflow-x-auto pb-2' : ''}`}>
                <TabsList className={`bg-blue-50 p-1 border border-blue-100 ${isMobile ? 'w-full mobile-tabs' : ''}`}>
                  <TabsTrigger 
                    value="create" 
                    className={`adalat-tab ${isMobile ? 'mobile-tab' : ''} ${activeTab === 'create' ? 'adalat-tab-active' : ''}`}
                  >
                    <FileEdit size={isMobile ? 18 : 16} className={`${language === 'ur' ? 'ml-1.5' : 'mr-1.5'}`} />
                    {language === 'ur' ? 'نیا دستاویز' : 'New Document'}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="saved" 
                    className={`adalat-tab ${isMobile ? 'mobile-tab' : ''} ${activeTab === 'saved' ? 'adalat-tab-active' : ''}`}
                  >
                    <Save size={isMobile ? 18 : 16} className={`${language === 'ur' ? 'ml-1.5' : 'mr-1.5'}`} />
                    {language === 'ur' ? 'محفوظ دستاویزات' : 'Saved Documents'}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="scanner" 
                    className={`adalat-tab ${isMobile ? 'mobile-tab' : ''} flex items-center gap-1 ${activeTab === 'scanner' ? 'adalat-tab-active' : ''}`}
                  >
                    <ScanText size={isMobile ? 18 : 16} />
                    {language === 'ur' ? 'اسکینر' : 'Scanner'}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="collaborate" 
                    className={`adalat-tab ${isMobile ? 'mobile-tab' : ''} ${activeTab === 'collaborate' ? 'adalat-tab-active' : ''}`}
                  >
                    {language === 'ur' ? 'تعاون' : 'Collaborate'}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="shared" 
                    className={`adalat-tab ${isMobile ? 'mobile-tab' : ''} flex items-center gap-1 ${activeTab === 'shared' ? 'adalat-tab-active' : ''}`}
                  >
                    <Share2 size={isMobile ? 18 : 16} />
                    {language === 'ur' ? 'اشتراک شدہ' : 'Shared'}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="chatbot" 
                    className={`adalat-tab ${isMobile ? 'mobile-tab' : ''} flex items-center gap-1 ${activeTab === 'chatbot' ? 'adalat-tab-active' : ''}`}
                  >
                    <Bot size={isMobile ? 18 : 16} />
                    {language === 'ur' ? 'چیٹ بوٹ' : 'Chatbot'}
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="create" className={language === 'ur' ? 'rtl' : 'ltr'}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <GeneratorForm onResult={handleResult} />
                    
                    {generatedResult && (
                      <ResultDisplay result={generatedResult} />
                    )}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="saved" className={language === 'ur' ? 'rtl' : 'ltr'}>
                <SavedDocuments onSelectDocument={handleSelectSavedDocument} />
              </TabsContent>
              
              <TabsContent value="scanner">
                <DocumentScanner />
              </TabsContent>

              <TabsContent value="collaborate">
                <CollaborativeDocuments />
              </TabsContent>
              
              <TabsContent value="shared">
                <SharedDocuments />
              </TabsContent>

              <TabsContent value="chatbot">
                <LegalChatbot />
              </TabsContent>
            </Tabs>
          ) : (
            <>
              {activeTab === 'create' && (
                <div className={language === 'ur' ? 'rtl' : 'ltr'}>
                  <div className="md:col-span-2">
                    <GeneratorForm onResult={handleResult} />
                    
                    {generatedResult && (
                      <ResultDisplay result={generatedResult} />
                    )}
                  </div>
                </div>
              )}
              
              {activeTab === 'saved' && (
                <div className={language === 'ur' ? 'rtl' : 'ltr'}>
                  <SavedDocuments onSelectDocument={handleSelectSavedDocument} />
                </div>
              )}
              
              {activeTab === 'scanner' && (
                <DocumentScanner />
              )}
              
              {activeTab === 'collaborate' && (
                <CollaborativeDocuments />
              )}
              
              {activeTab === 'shared' && (
                <SharedDocuments />
              )}
              
              {activeTab === 'chatbot' && (
                <LegalChatbot />
              )}

              <MobileNavigation 
                activeTab={activeTab} 
                onTabChange={setActiveTab} 
              />
            </>
          )}
        </main>
        
        <Footer />
      </div>
      
      {/* Show QR Code in footer on mobile */}
      {isMobile && (
        <div className="fixed bottom-20 right-4 z-10">
          <QRCodeShare />
        </div>
      )}
    </div>
  );
};

const Index = () => {
  return (
    <LanguageProvider>
      <IndexContent />
    </LanguageProvider>
  );
};

export default Index;
